/*
 * Dealer locator: dealer list with search and an OpenStreetMap map.
 *
 * A search for a place or postcode always yields results: the query is turned
 * into coordinates via assets/dealer-locator-geo.json (GeoNames, CC BY 4.0,
 * bundled so no search text leaves the site; places and postcodes of every
 * country with dealers) and all dealers are sorted by distance. Without a
 * search the map shows the country chosen in the header, as far as there are
 * dealers in it. Dealers come from assets/dealers.json. The map, and with it every
 * request to the OSM tile servers, is only created after the visitor consents.
 */
if (!customElements.get('dealer-locator')) {
  const CONSENT_KEY = 'haro-dealer-locator-map-consent';
  const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
  const NEAREST_IN_VIEW = 5;
  const MIN_PREFIX_LENGTH = 3;

  /*
   * How a postcode is written per country. The capture groups joined together
   * are the key in the index, so "01-524" and "01524" both find Warsaw. Where
   * several countries share a format, findZip() decides between them; a longer
   * match ("2011 TD" for the Netherlands) beats a shorter one.
   */
  const ZIP_FORMATS = [
    { countries: ['DE', 'FR', 'ES', 'HR', 'TR', 'RE'], pattern: /\b(\d{5})\b/ },
    { countries: ['PL'], pattern: /\b(\d{2})[ -]?(\d{3})\b/ },
    { countries: ['CZ', 'SK'], pattern: /\b(\d{3})[ -]?(\d{2})\b/ },
    { countries: ['NL'], pattern: /\b(\d{4}) ?[A-Z]{2}\b/ },
    { countries: ['PT'], pattern: /\b(\d{4})-\d{3}\b/ },
    { countries: ['LU'], pattern: /\bL-? ?(\d{4})\b/ },
    { countries: ['AT', 'BE', 'CH', 'CY', 'DK', 'HU', 'LU', 'NL', 'PT'], pattern: /\b(\d{4})\b/ },
    { countries: ['GB'], pattern: /\b([A-Z]{1,2}\d[A-Z\d]?)(?: ?\d[A-Z]{2})?\b/ },
    { countries: ['IE'], pattern: /\b(D6W|[AC-FHKNPRTV-Y]\d{2})\b/ },
    { countries: ['MT'], pattern: /\b([A-Z]{3}) ?\d{4}\b/ },
  ];

  // Country codes as they are written in front of a postcode: "CH-8001", "F-75001".
  const COUNTRY_PREFIXES = {
    a: 'AT', b: 'BE', ch: 'CH', cy: 'CY', cz: 'CZ', d: 'DE', dk: 'DK', e: 'ES', f: 'FR',
    gb: 'GB', h: 'HU', hr: 'HR', irl: 'IE', l: 'LU', m: 'MT', nl: 'NL', p: 'PT', pl: 'PL',
    sk: 'SK', tr: 'TR', uk: 'GB',
  };

  const normalize = (value) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      // "Muenchen" and "München" should find the same place.
      .replace(/([aou])e/g, '$1')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  /*
   * The geo index is stored as text, see the note in the file: places as
   * "name|country|lat|lng;…", postcodes per country as "code,lat,lng;…" with
   * coordinates in hundredths of a degree, base 36, each relative to the entry
   * before it. Plain JSON of the same data would be about three times as large.
   */
  const decodePlaces = (encoded) =>
    encoded.split(';').map((entry) => {
      const [name, country, lat, lng] = entry.split('|');
      return { name, country, key: normalize(name), coords: [parseInt(lat, 36) / 100, parseInt(lng, 36) / 100] };
    });

  const decodeZips = (encoded) => {
    const zips = {};
    let lat = 0;
    let lng = 0;
    encoded.split(';').forEach((entry) => {
      const [key, deltaLat, deltaLng] = entry.split(',');
      lat += parseInt(deltaLat, 36);
      lng += parseInt(deltaLng, 36);
      zips[key] = [lat / 100, lng / 100];
    });
    return zips;
  };

  /* Country names in German, English and the page language: "Frankreich", "France". */
  const countryNameIndex = (countries) => {
    const names = new Map();
    ['de', 'en', document.documentElement.lang || 'en'].forEach((language) => {
      let display;
      try {
        display = new Intl.DisplayNames([language], { type: 'region' });
      } catch (error) {
        return;
      }
      countries.forEach((country) => {
        const name = normalize(display.of(country) || '');
        // Short names would match words inside an address.
        if (name.length >= 4) names.set(name, country);
      });
    });
    return [...names].sort((a, b) => b[0].length - a[0].length);
  };

  const ARROW_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/></svg>';

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };

  const telHref = (phone) => {
    let number = phone.replace(/\(0\)/g, '').replace(/[^\d+]/g, '');
    if (number.startsWith('00')) number = '+' + number.slice(2);
    return 'tel:' + number;
  };

  const hostname = (url) => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch (error) {
      return url;
    }
  };

  const directionsUrl = (dealer) =>
    'https://www.google.com/maps/dir/?api=1&destination=' +
    encodeURIComponent(`${dealer.street}, ${dealer.zip} ${dealer.city}, ${dealer.country}`);

  const countryNames = (() => {
    try {
      return new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' });
    } catch (error) {
      return null;
    }
  })();

  const countryName = (dealer) => (countryNames && dealer.countryCode && countryNames.of(dealer.countryCode)) || dealer.country;

  const addressLines = (dealer) => [dealer.street, `${dealer.zip} ${dealer.city}`.trim(), countryName(dealer)];

  const appendLines = (element, lines) =>
    lines.forEach((line, index) => {
      if (index) element.append(document.createElement('br'));
      element.append(line);
    });

  // Great-circle distance in km.
  const distanceKm = (from, to) => {
    const rad = (degrees) => (degrees * Math.PI) / 180;
    const dLat = rad(to.lat - from.lat);
    const dLng = rad(to.lng - from.lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(from.lat)) * Math.cos(rad(to.lat)) * Math.sin(dLng / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  customElements.define(
    'dealer-locator',
    class DealerLocator extends HTMLElement {
      connectedCallback() {
        this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
        this.list = this.querySelector('[data-list]');
        this.status = this.querySelector('[data-status]');
        this.searchInput = this.querySelector('[data-search]');
        this.locateButton = this.querySelector('[data-locate]');
        this.resetButton = this.querySelector('[data-reset]');
        this.mapElement = this.querySelector('[data-map]');
        this.consent = this.querySelector('[data-consent]');

        this.dealers = [];
        this.query = '';
        // Result of the last search: { type: 'all' | 'place' | 'text' | 'not_found', origin?, label?, terms? }
        this.search = { type: 'all' };
        this.userOrigin = null;
        this.activeId = null;
        this.map = null;
        this.markers = new Map();
        this.geoIndex = null;
        this.dealersByCountry = new Map();
        this.searchRun = 0;

        let searchTimer;
        this.searchInput.addEventListener('input', () => {
          clearTimeout(searchTimer);
          searchTimer = setTimeout(() => this.runSearch(this.searchInput.value), 250);
        });
        this.searchInput.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          clearTimeout(searchTimer);
          this.runSearch(this.searchInput.value);
        });

        this.locateButton.addEventListener('click', () => this.locate());
        this.resetButton.addEventListener('click', () => this.reset());
        this.consent.querySelector('[data-consent-accept]').addEventListener('click', () => this.acceptConsent());

        this.load();
      }

      disconnectedCallback() {
        if (this.map) this.map.remove();
      }

      async load() {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) throw new Error('HTTP ' + response.status);
          const dealers = await response.json();
          this.dealers = dealers.map((dealer) => ({
            ...dealer,
            searchText: normalize([dealer.name, ...addressLines(dealer), dealer.country].join(' ')),
          }));
          this.dealersByCountry = new Map();
          this.dealers.forEach((dealer) => {
            const list = this.dealersByCountry.get(dealer.countryCode) || [];
            list.push(dealer);
            this.dealersByCountry.set(dealer.countryCode, list);
          });
        } catch (error) {
          console.error('[dealer-locator]', error);
          this.status.textContent = this.strings.load_error;
          return;
        }

        this.render();
        if (this.hasStoredConsent()) this.initMap();
      }

      /* Search ------------------------------------------------------------- */

      async runSearch(value) {
        const run = ++this.searchRun;
        this.query = value.trim();
        const search = await this.resolve(this.query);
        // A slower, older search must not overwrite the result of a newer one.
        if (run !== this.searchRun) return;

        this.search = search;
        this.setState(this.query || this.userOrigin ? 'results' : 'initial');
        this.render();
        this.list.scrollTo({ top: 0 });
      }

      /* 'initial': full-width map with floating search. 'results': list next to the map. */
      setState(state) {
        if (this.dataset.state === state) return;
        this.dataset.state = state;
        if (!this.map) return;
        // Leaflet has to re-measure once the map container changed its width.
        requestAnimationFrame(() => {
          this.map.invalidateSize();
          this.updateMarkers(this.results());
        });
      }

      reset() {
        this.searchRun++;
        this.searchInput.value = '';
        this.query = '';
        this.search = { type: 'all' };
        this.userOrigin = null;
        this.activeId = null;
        if (this.map) this.map.closePopup();
        this.setState('initial');
        this.render();
        this.searchInput.focus();
      }

      /*
       * Order matters:
       *   1. postcode – the country comes from the query ("CH 8001", "8001 Wien"),
       *      from the country chosen in the header, or from the dealer countries
       *   2. German postcode prefix – "7015…"
       *   3. exact place name       – "Stuttgart" lists every dealer by distance
       *   4. dealer name or street  – "Radhaus", "Bike"
       *   5. place name prefix      – "Freib" → Freiburg im Breisgau
       * Anything else still returns all dealers, marked as not found.
       */
      async resolve(query) {
        if (!query) return { type: 'all' };

        const geo = await this.loadGeoIndex();
        const normalized = normalize(query);
        // "Lyon Frankreich" searches for Lyon in France, not for the word "Frankreich".
        const { country, rest } = this.readCountry(normalized, geo);

        if (geo) {
          // A German postcode prefix, as long as the query points to Germany.
          const prefixCountry = country || this.homeCountry();
          const zipPrefix = (!prefixCountry || prefixCountry === 'DE') && rest.match(/^(\d{2,4})$/);
          if (zipPrefix) {
            const key = geo.zipKeys.DE.find((entry) => entry.startsWith(zipPrefix[1]));
            if (key) return this.placeResult(geo.zips.DE[key], `${zipPrefix[1]}…`);
          }

          const zip = this.findZip(query, country, rest.replace(/\d+/g, ' ').trim(), geo);
          if (zip) return this.placeResult(zip.coords, zip.label);

          const exactPlace = this.choosePlace(geo.placeByKey.get(rest), country);
          if (exactPlace) return this.placeResult(exactPlace.coords, exactPlace.name);
        }

        const terms = normalized.split(' ').filter(Boolean);
        if (terms.length && this.dealers.some((dealer) => terms.every((term) => dealer.searchText.includes(term)))) {
          return { type: 'text', terms };
        }

        if (geo && rest.length >= MIN_PREFIX_LENGTH) {
          const place = this.choosePlace(geo.places.filter((entry) => entry.key.startsWith(rest)), country);
          if (place) return this.placeResult(place.coords, place.name);
        }

        return { type: 'not_found' };
      }

      /* A country named in the query, plus the query without it. */
      readCountry(normalized, geo) {
        if (!geo) return { country: null, rest: normalized };
        for (const [name, country] of geo.countryNames) {
          const words = new RegExp(`(^| )${name}( |$)`);
          if (words.test(normalized)) return { country, rest: normalized.replace(words, ' ').trim() };
        }
        return { country: null, rest: normalized };
      }

      /*
       * The same postcode exists in several countries: "8001" is Zurich as well as
       * Aarhus. The one that fits the query wins, then the country chosen in the
       * header, then the country with the most dealers.
       */
      findZip(query, country, place, geo) {
        const text = query.toUpperCase();
        const candidates = [];
        ZIP_FORMATS.forEach(({ countries, pattern }) => {
          const match = text.match(pattern);
          if (!match) return;
          const key = match.slice(1).filter(Boolean).join('');
          countries.forEach((code) => {
            const coords = geo.zips[code] && geo.zips[code][key];
            if (coords) candidates.push({ country: code, coords, label: match[0].trim(), length: match[0].length });
          });
        });
        if (!candidates.length) return null;

        // "1020 Wien" is Vienna, not the Danish 1020.
        const named = new Set(((place && geo.placeByKey.get(place)) || []).map((entry) => entry.country));
        const prefix = text.match(/\b([A-Z]{1,3})-\s*\d/);
        const hint = country || (prefix && COUNTRY_PREFIXES[prefix[1].toLowerCase()]) || this.homeCountry();
        const dealerCount = (entry) => (this.dealersByCountry.get(entry.country) || []).length;
        candidates.sort((a, b) => b.length - a.length || dealerCount(b) - dealerCount(a));
        const longest = candidates.filter((entry) => entry.length === candidates[0].length);
        return (
          longest.find((entry) => named.has(entry.country)) ||
          longest.find((entry) => entry.country === hint) ||
          candidates[0]
        );
      }

      /* Of several places with the same name, the one in the wanted country. */
      choosePlace(places, country) {
        if (!places || !places.length) return null;
        const hint = country || this.homeCountry();
        // Only among the larger ones: "Neustadt" should not end up in a hamlet.
        return places.slice(0, 50).find((place) => place.country === hint) || places[0];
      }

      /* The country chosen in the header, as long as it has dealers. */
      homeCountry() {
        const country = (this.dataset.country || '').toUpperCase();
        return this.dealersByCountry.has(country) ? country : null;
      }

      placeResult([lat, lng], label) {
        return { type: 'place', origin: { lat, lng }, label };
      }

      loadGeoIndex() {
        if (this.geoIndex) return Promise.resolve(this.geoIndex);
        if (!this.geoIndexPromise) {
          this.geoIndexPromise = fetch(this.dataset.geoUrl)
            .then((response) => {
              if (!response.ok) throw new Error('HTTP ' + response.status);
              return response.json();
            })
            .then((data) => {
              // Places arrive sorted by size, so prefix matches favour larger towns.
              const places = decodePlaces(data.places);
              const placeByKey = new Map();
              places.forEach((place) => {
                const same = placeByKey.get(place.key);
                if (same) same.push(place);
                else placeByKey.set(place.key, [place]);
              });

              const zips = {};
              Object.entries(data.zips).forEach(([country, encoded]) => {
                zips[country] = decodeZips(encoded);
              });
              // Only German postcodes are also searched by prefix.
              const zipKeys = { DE: Object.keys(zips.DE) };

              this.geoIndex = { zips, zipKeys, places, placeByKey, countryNames: countryNameIndex(Object.keys(zips)) };
              return this.geoIndex;
            })
            .catch((error) => {
              // Without the index the search falls back to dealer names only.
              console.error('[dealer-locator]', error);
              this.geoIndexPromise = null;
              return null;
            });
        }
        return this.geoIndexPromise;
      }

      /* Results ------------------------------------------------------------ */

      origin() {
        return this.search.type === 'place' ? this.search.origin : this.userOrigin;
      }

      results() {
        let dealers = this.dealers;
        if (this.search.type === 'text') {
          dealers = dealers.filter((dealer) => this.search.terms.every((term) => dealer.searchText.includes(term)));
        }

        const origin = this.origin();
        const results = dealers.map((dealer) => ({ dealer, distance: origin ? distanceKm(origin, dealer) : null }));
        if (origin) results.sort((a, b) => a.distance - b.distance);
        return results;
      }

      render() {
        const results = this.results();
        this.list.replaceChildren(...results.map((result) => this.renderCard(result)));
        this.status.textContent = this.statusText(results.length);
        this.updateMarkers(results);
      }

      statusText(count) {
        const countText = count === 1 ? this.strings.result_one : this.strings.results.replace('{count}', count);
        const located = this.userOrigin ? ` · ${this.strings.located}` : '';

        switch (this.search.type) {
          case 'place':
            return this.strings.nearest_to.replace('{place}', this.search.label);
          case 'not_found':
            return this.strings.not_found.replace('{query}', this.query) + located;
          case 'text':
            return countText;
          default:
            return countText + located;
        }
      }

      renderCard({ dealer, distance }) {
        const item = createElement('li', 'dealer-locator__item');
        const card = createElement('article', 'dealer-card');
        card.dataset.id = dealer.id;
        if (dealer.id === this.activeId) card.classList.add('is-active');

        const head = createElement('div', 'dealer-card__head');
        const title = createElement('button', 'dealer-card__title');
        title.type = 'button';
        title.append(createElement('h3', 'dealer-card__name h4', dealer.name));
        title.addEventListener('click', () => this.showOnMap(dealer));

        const directions = createElement('a', 'dealer-locator__round-button dealer-card__directions');
        directions.href = directionsUrl(dealer);
        directions.target = '_blank';
        directions.rel = 'noopener';
        directions.setAttribute('aria-label', `${this.strings.directions}: ${dealer.name}`);
        directions.title = this.strings.directions;
        directions.innerHTML = ARROW_ICON;
        head.append(title, directions);

        const meta = distance != null ? `${this.formatDistance(distance)} • ${dealer.city}` : dealer.city;

        const body = createElement('div', 'dealer-card__body');
        const address = createElement('address', 'dealer-card__address');
        appendLines(address, addressLines(dealer));
        body.append(address);

        const contact = createElement('ul', 'dealer-card__contact list-unstyled');
        const addLink = (href, text, external) => {
          const link = createElement('a', 'link', text);
          link.href = href;
          if (external) {
            link.target = '_blank';
            link.rel = 'noopener';
          }
          const row = createElement('li');
          row.append(link);
          contact.append(row);
        };
        if (dealer.email) addLink('mailto:' + dealer.email, dealer.email);
        if (dealer.phone) addLink(telHref(dealer.phone), dealer.phone);
        if (dealer.website) addLink(dealer.website, hostname(dealer.website), true);
        if (contact.children.length) body.append(contact);

        card.append(head, createElement('p', 'dealer-card__meta', meta), body);
        item.append(card);
        return item;
      }

      formatDistance(km) {
        const formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, {
          maximumFractionDigits: km < 10 ? 1 : 0,
        }).format(km);
        return this.strings.distance.replace('{distance}', formatted);
      }

      showOnMap(dealer) {
        if (!this.map) {
          const accept = this.consent.querySelector('[data-consent-accept]');
          this.consent.scrollIntoView({ behavior: 'smooth', block: 'center' });
          accept.focus({ preventScroll: true });
          return;
        }

        this.mapElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        this.select(dealer.id);
        const marker = this.markers.get(dealer.id);
        this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 13));
        marker.openPopup();
      }

      select(id, { scrollList = false } = {}) {
        this.activeId = id;

        this.list.querySelectorAll('.dealer-card').forEach((card) => {
          card.classList.toggle('is-active', card.dataset.id === id);
        });
        this.markers.forEach((marker, markerId) => {
          const element = marker.getElement();
          if (element) element.classList.toggle('is-active', markerId === id);
        });

        if (scrollList && id) {
          const card = this.list.querySelector(`[data-id="${CSS.escape(id)}"]`);
          if (!card) return;
          // Only scroll inside the list when it is its own scroll container (desktop),
          // otherwise the whole page would jump away from the map.
          if (this.list.scrollHeight > this.list.clientHeight) {
            this.list.scrollTo({ top: card.parentElement.offsetTop, behavior: 'smooth' });
          }
        }
      }

      /* Consent ------------------------------------------------------------ */

      hasStoredConsent() {
        try {
          return localStorage.getItem(CONSENT_KEY) === '1';
        } catch (error) {
          return false;
        }
      }

      acceptConsent() {
        if (this.consent.querySelector('[data-consent-remember]').checked) {
          try {
            localStorage.setItem(CONSENT_KEY, '1');
          } catch (error) {
            // Storage blocked: the map still loads, just not remembered.
          }
        }
        this.initMap();
      }

      /* Map ---------------------------------------------------------------- */

      initMap() {
        if (this.map || !window.L) return;

        this.consent.hidden = true;
        // Zoom sits top right: top left is covered by the floating search field.
        this.map = L.map(this.mapElement, { scrollWheelZoom: false, zoomControl: false });
        L.control.zoom({ position: 'topright' }).addTo(this.map);
        L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(this.map);

        // Page scrolling should not get trapped by the map until it is used.
        this.map.on('click focus', () => this.map.scrollWheelZoom.enable());
        this.mapElement.addEventListener('mouseleave', () => this.map.scrollWheelZoom.disable());

        const icon = L.divIcon({
          className: 'dealer-locator__pin',
          html: '<span></span>',
          iconSize: [28, 28],
          iconAnchor: [14, 34],
          popupAnchor: [0, -32],
        });

        this.dealers.forEach((dealer) => {
          const marker = L.marker([dealer.lat, dealer.lng], { icon, title: dealer.name, alt: dealer.name });
          marker.bindPopup(() => this.renderPopup(dealer));
          marker.on('click', () => this.select(dealer.id, { scrollList: this.dataset.state === 'results' }));
          this.markers.set(dealer.id, marker);
        });

        this.markerLayer = L.layerGroup().addTo(this.map);
        this.updateMarkers(this.results());
      }

      renderPopup(dealer) {
        const popup = createElement('div', 'dealer-popup');
        popup.append(createElement('strong', 'dealer-popup__name', dealer.name));
        const address = createElement('p', 'dealer-popup__address');
        appendLines(address, addressLines(dealer));
        const directions = createElement('a', 'link', this.strings.directions);
        directions.href = directionsUrl(dealer);
        directions.target = '_blank';
        directions.rel = 'noopener';
        popup.append(address, directions);
        return popup;
      }

      updateMarkers(results) {
        if (!this.map) return;

        this.markerLayer.clearLayers();
        results.forEach(({ dealer }) => this.markerLayer.addLayer(this.markers.get(dealer.id)));
        this.select(this.activeId);

        const origin = this.origin();
        this.updateOriginMarker(origin);
        if (results.length === 0) return;

        const home = this.search.type === 'all' && !origin ? this.dealersByCountry.get(this.homeCountry()) : null;
        const shown = origin ? results.slice(0, NEAREST_IN_VIEW) : results;
        // Without a search the map shows the country chosen in the header.
        const points = (home || shown.map(({ dealer }) => dealer)).map((dealer) => [dealer.lat, dealer.lng]);
        if (origin) points.push([origin.lat, origin.lng]);
        this.map.fitBounds(points, { padding: [32, 32], maxZoom: 13 });
      }

      updateOriginMarker(origin) {
        if (this.originMarker) {
          this.originMarker.remove();
          this.originMarker = null;
        }
        if (!origin) return;

        const label = this.search.type === 'place' ? this.search.label : this.strings.your_location;
        this.originMarker = L.circleMarker([origin.lat, origin.lng], {
          radius: 8,
          className: 'dealer-locator__origin',
        })
          .bindTooltip(label)
          .addTo(this.map);
      }

      /* Geolocation -------------------------------------------------------- */

      locate() {
        if (!navigator.geolocation) {
          this.status.textContent = this.strings.locate_error;
          return;
        }

        this.locateButton.setAttribute('aria-busy', 'true');
        this.locateButton.disabled = true;
        this.status.textContent = this.strings.locating;

        const done = () => {
          this.locateButton.removeAttribute('aria-busy');
          this.locateButton.disabled = false;
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            done();
            this.userOrigin = { lat: position.coords.latitude, lng: position.coords.longitude };
            // The own location replaces any typed search.
            this.searchRun++;
            this.searchInput.value = '';
            this.query = '';
            this.search = { type: 'all' };
            this.setState('results');
            this.render();
            this.list.scrollTo({ top: 0 });
          },
          () => {
            done();
            this.status.textContent = this.strings.locate_error;
          },
          { timeout: 10000, maximumAge: 600000 }
        );
      }
    }
  );
}
